import {
  createContext,
  DetailedHTMLProps,
  HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import clsx from "clsx";
import { useHydrated } from "remix-utils/use-hydrated";

const screenSize = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
} as const;

function useMediaQuery(width: keyof typeof screenSize) {
  let isHydrated = useHydrated();

  const media = useMemo(
    () => (isHydrated ? window.matchMedia(`(min-width: ${screenSize[width]})`) : null),
    [isHydrated, width],
  );

  const [targetReached, setTargetReached] = useState(media?.matches ?? null);
  const updateTarget = useCallback((e: MediaQueryListEvent) => setTargetReached(e.matches), []);

  useEffect(() => {
    // https://skedify.sentry.io/issues/4359275750/ -> `addEventListener` is not a fn on Safari 13
    media?.addEventListener?.("change", updateTarget);

    // Check on mount (callback is not called until a change occurs)
    setTargetReached(media?.matches ?? null);

    return () => media?.removeEventListener("change", updateTarget);
  }, [updateTarget, media]);

  return targetReached;
}

const MediaQueryContext = createContext<null | boolean>(null);

export function MediaQueryProvider({ children }: { children: React.ReactNode }) {
  const isDesktop = useMediaQuery("md");

  return <MediaQueryContext.Provider value={isDesktop}>{children}</MediaQueryContext.Provider>;
}

interface MediaProps extends DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> {
  children: React.ReactNode;
  showFor: "desktop" | "mobile";
}

export function Media({ children, className: classNameProp, showFor, ...props }: MediaProps) {
  const isDesktop = useContext(MediaQueryContext);

  const className = clsx(
    showFor === "desktop" && "hidden md:block",
    showFor === "mobile" && "block md:hidden",
    classNameProp,
  );

  if (isDesktop === null) {
    return (
      <section {...props} className={className}>
        {children}
      </section>
    );
  }

  if (showFor === "desktop" && isDesktop)
    return (
      <section {...props} className={className}>
        {children}
      </section>
    );

  if (showFor === "mobile" && !isDesktop)
    return (
      <section {...props} className={className}>
        {children}
      </section>
    );

  return null;
}
